import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@sunstone/ui';
import { useRef, useState } from 'react';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface EditableComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  className?: string;
}

/**
 * Free-text input with a filtered suggestion dropdown. Unlike SearchableCombobox/
 * ReleaseCombobox/MultiSelectCombobox — all strict pickers behind a passive
 * trigger button — every keystroke here commits directly as the value; picking
 * a suggestion just fills it in, it never restricts what can be typed.
 */
export function EditableCombobox({
  value,
  onChange,
  options,
  placeholder,
  className,
}: EditableComboboxProps) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const query = value.trim().toLowerCase();
  const filtered = query
    ? options.filter(
        (opt) => opt.value.toLowerCase().includes(query) || opt.label.toLowerCase().includes(query),
      )
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder={placeholder}
          className={className}
        />
      </PopoverAnchor>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (inputRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
      >
        <Command shouldFilter={false}>
          <CommandList>
            <CommandEmpty>
              No matching suggestions — you can still type a custom value.
            </CommandEmpty>
            <CommandGroup>
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                    inputRef.current?.focus();
                  }}
                >
                  {opt.label}
                  {opt.label !== opt.value && (
                    <span className="ml-auto text-xs text-muted-foreground">{opt.value}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
